import _defineProperty from "/workdir/node_modules/next/node_modules/@babel/runtime/helpers/esm/defineProperty";
import React from 'react';
import Heading from "@scentregroup/ui/components/heading";
import Container from "@scentregroup/ui/components/container";
import Icon from "@scentregroup/ui/components/icon";
import classNames from 'classnames';
import DeckScrollWrapper from '../deck-scroll-wrapper';
import ExternalLink from '../external-link';
import analyticsUtils from '../../helpers/analytics-utils';
import { TABINDEX_TABBABLE, TABINDEX_NOT_TABBABLE } from '../../helpers/a11y-helpers';
import { getClientConfigValue } from '../../config';
import { STACKLA_PROXY } from '../../constants';
import { withTouchDetection } from '../../helpers/with-touch-detection';
import { lazyloadWrapper } from '../../helpers/lazyload-wrapper';
import style from './index.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

class SocialFeed extends React.Component {
  constructor(props) {
    super(props);

    _defineProperty(this, "trackModuleImpression", () => {
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.MODULE_IMPRESSION,
        eventCategory: analyticsUtils.categories.USER_GENERATED_CONTENT,
        eventAction: analyticsUtils.actions.INSTA_DECK_SHOW
      });
    });

    _defineProperty(this, "trackModuleClick", () => {
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.USER_CLICK,
        eventCategory: analyticsUtils.categories.USER_GENERATED_CONTENT,
        eventAction: analyticsUtils.actions.INSTA_DECK_TILE
      });
    });

    _defineProperty(this, "showDetails", id => {
      this.setState({
        activeEntry: id
      });
    });

    _defineProperty(this, "hideDetails", () => {
      this.setState({
        activeEntry: null
      });
    });

    _defineProperty(this, "focusDetails", id => {
      this.setState({
        focusedEntry: id,
        activeEntry: id
      });
      const index = this.entryIds.indexOf(id);
      const elem = this.gridNode.current.children[index];

      if (elem && elem.focus) {
        elem.focus();
      }
    });

    _defineProperty(this, "keyPressed", event => {
      const {
        focusedEntry
      } = this.state;
      const entryIds = this.entryIds;
      const focusedIndex = entryIds.indexOf(focusedEntry);

      const focusOn = index => {
        this.focusDetails(entryIds[index]);
        event.preventDefault();
      };

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          if (focusedIndex > 0) {
            focusOn(focusedIndex - 1);
          }

          break;

        case 'ArrowRight':
        case 'ArrowDown':
          if (focusedIndex < entryIds.length - 1) {
            focusOn(focusedIndex + 1);
          }

          break;

        case 'Home':
          focusOn(0);
          break;

        case 'End':
          focusOn(entryIds.length - 1);
          break;

        default:
          break;
      }
    });

    _defineProperty(this, "itemFocused", id => {
      if (this.props.isUsingTouch && id !== this.state.activeEntry) {
        this.trackModuleClick();
      }

      this.setState({
        focusedEntry: id,
        activeEntry: id
      });
    });

    _defineProperty(this, "itemClicked", id => {
      if (this.props.isUsingTouch && id !== this.state.activeEntry) {
        this.itemFocused(id);
      }
    });

    _defineProperty(this, "gridBlurred", () => {
      // setTimeout is required to make sure the DOM has registered focus on a new element first
      setTimeout(() => {
        if (this.gridNode.current) {
          const hasInnerFocus = this.gridNode.current.contains(document.activeElement);

          if (!hasInnerFocus) {
            this.hideDetails();
          }
        }
      }, 0);
    });

    _defineProperty(this, "entryFail", entry => {
      const rawScentreApi = getClientConfigValue('NEXT_PUBLIC_CONTENT_SERVICE').replace(/\/graphql(\/)?$/, '');
      this.setState({
        brokenEntries: [...this.state.brokenEntries, entry.id]
      });
      fetch("".concat(rawScentreApi, "/").concat(STACKLA_PROXY, "_id=").concat(entry.id, "&stack=").concat(this.props.stackShortName));
    });

    this.entryIds = props.entries.map(entry => entry.id);
    this.state = {
      // The feed item currently receiving keyboard focus
      focusedEntry: this.entryIds[0] || null,
      // The feed item currently displaying text details (can be different due to mouse hover)
      activeEntry: null,
      // Images that failed to load, for reporting back to Stackla
      brokenEntries: []
    };
    this.gridNode = /*#__PURE__*/React.createRef();
  }

  componentDidMount() {
    this.trackModuleImpression();
  }

  render() {
    const SOCIAL_FEED_LENGTH = 20; // Evenly divisible by 4 and 5 for different layouts

    const {
      color,
      title,
      isUsingTouch
    } = this.props;
    const {
      focusedEntry,
      activeEntry,
      brokenEntries
    } = this.state;
    const visibleEntries = this.props.entries.filter(e => !brokenEntries.includes(e.id)).slice(0, SOCIAL_FEED_LENGTH);
    this.entryIds = visibleEntries.map(entry => entry.id);
    return /*#__PURE__*/_jsxs(Container, {
      centred: true,
      type: "large",
      className: style.socialDeckContainer,
      children: [/*#__PURE__*/_jsx(Heading, {
        type: "large",
        className: style.socialDeckHeading,
        children: title
      }), /*#__PURE__*/_jsx(DeckScrollWrapper, {
        className: style.socialDeckWrapper,
        eventCategory: analyticsUtils.categories.USER_GENERATED_CONTENT,
        eventAction: analyticsUtils.actions.INSTA_DECK_SCROLL,
        bgColor: color,
        children: /*#__PURE__*/_jsx("div", {
          role: "grid",
          "aria-label": "Social media images",
          children: /*#__PURE__*/_jsx("div", {
            className: style.socialDeck,
            role: "row",
            ref: this.gridNode,
            onBlur: this.gridBlurred,
            children: visibleEntries.map(entry => /*#__PURE__*/_jsx("div", {
              className: classNames(style.deckItem, {
                [style.deckItemActive]: activeEntry === entry.id
              }),
              role: "gridcell",
              tabIndex: focusedEntry === entry.id ? TABINDEX_TABBABLE : TABINDEX_NOT_TABBABLE,
              onFocus: () => this.itemFocused(entry.id),
              onClick: () => this.itemClicked(entry.id),
              onKeyDown: this.keyPressed,
              onMouseEnter: () => {
                if (!isUsingTouch) {
                  this.showDetails(entry.id);
                }
              },
              onMouseLeave: () => {
                if (!isUsingTouch) {
                  this.hideDetails();
                }
              },
              children: /*#__PURE__*/_jsxs("figure", {
                children: [/*#__PURE__*/_jsx("figcaption", {
                  className: style.deckInner,
                  children: /*#__PURE__*/_jsxs("div", {
                    className: style.deckText,
                    children: [entry.username && /*#__PURE__*/_jsxs("div", {
                      className: style.deckHeading,
                      children: ["@", entry.username]
                    }), /*#__PURE__*/_jsx("div", {
                      className: style.deckCaption,
                      children: entry.caption
                    }), /*#__PURE__*/_jsxs(ExternalLink, {
                      url: entry.url,
                      className: style.deckLink,
                      eventCategory: analyticsUtils.categories.USER_GENERATED_CONTENT,
                      eventAction: analyticsUtils.actions.INSTA_DECK_LINK,
                      children: ["View on Instagram", /*#__PURE__*/_jsx(Icon, {
                        className: style.linkIcon,
                        size: "small",
                        type: "arrow-right"
                      })]
                    })]
                  })
                }), /*#__PURE__*/_jsx("img", {
                  alt: entry.title || entry.caption,
                  src: entry.image.url,
                  onError: () => this.entryFail(entry)
                })]
              })
            }, entry.id))
          })
        })
      })]
    });
  }

}

export const RawSocialFeed = SocialFeed;
export default lazyloadWrapper(withTouchDetection(RawSocialFeed));